/**
 * InlineSelectCell - Inline select dropdown for data tables.
 *
 * Click to edit, change or blur to save, Escape to cancel.
 * Per STREAM_UX_PATTERNS.md S4.1 inline edit pattern.
 *
 * Feature-local component - sibling to InlineEditCell for enum fields.
 */

import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type CSSProperties,
  type ReactNode,
} from 'react'

// ── Types ───────────────────────────────────────────────────────────────────

export interface InlineSelectCellProps {
  /** Current value */
  value: string
  /** Called when value is saved */
  onSave: (value: string) => Promise<void> | void
  /** Select options */
  options: Array<{ value: string; label: string }>
  /** Placeholder when empty */
  placeholder?: string
  /** Validation function - returns error message or null */
  validate?: (value: string) => string | null
  /** Whether editing is disabled */
  disabled?: boolean
}

// ── Component ───────────────────────────────────────────────────────────────

export function InlineSelectCell({
  value,
  onSave,
  options,
  placeholder = 'Click to edit',
  validate,
  disabled = false,
}: InlineSelectCellProps): ReactNode {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const selectRef = useRef<HTMLSelectElement>(null)

  // Update edit value when prop changes (while not editing)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value)
    }
  }, [value, isEditing])

  // Focus select when entering edit mode
  useEffect(() => {
    if (isEditing && selectRef.current) {
      selectRef.current.focus()
    }
  }, [isEditing])

  const handleStartEdit = useCallback(
    (e?: React.MouseEvent) => {
      if (disabled || isSaving) return
      // Prevent the click from bubbling to the row (which would trigger row
      // selection in DataTable).
      e?.stopPropagation()
      setIsEditing(true)
      setError(null)
    },
    [disabled, isSaving]
  )

  const handleCancel = useCallback(() => {
    setIsEditing(false)
    setEditValue(value)
    setError(null)
  }, [value])

  const handleSave = useCallback(async () => {
    // Skip if no change
    if (editValue === value) {
      setIsEditing(false)
      return
    }

    // Validate
    if (validate) {
      const validationError = validate(editValue)
      if (validationError) {
        setError(validationError)
        return
      }
    }

    setIsSaving(true)
    setError(null)

    try {
      await onSave(editValue)
      setIsEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setIsSaving(false)
    }
  }, [editValue, value, validate, onSave])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setEditValue(e.target.value)
      setError(null)
    },
    []
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSave()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handleCancel()
      }
    },
    [handleSave, handleCancel]
  )

  const handleBlur = useCallback(() => {
    // Small delay to allow click events to register
    setTimeout(() => {
      if (isEditing && !isSaving) {
        handleSave()
      }
    }, 100)
  }, [isEditing, isSaving, handleSave])

  // Editing mode
  if (isEditing) {
    return (
      <div
        style={styles.editContainer}
        onClick={(e) => e.stopPropagation()}
      >
        <select
          ref={selectRef}
          value={editValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={isSaving}
          style={{
            ...styles.select,
            ...(error ? styles.selectError : {}),
          }}
          className="inline-select-input"
        >
          {/* Render placeholder option only when current value is empty so it
              represents an unselected state, not a permanent header. */}
          {!editValue && placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {error && <div style={styles.error}>{error}</div>}
      </div>
    )
  }

  // Display mode
  const currentOption = options.find((opt) => opt.value === value)

  return (
    <button
      type="button"
      onClick={(e) => handleStartEdit(e)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled}
      style={{
        ...styles.display,
        ...(isHovered && !disabled ? styles.displayHover : {}),
        ...(disabled ? styles.displayDisabled : {}),
      }}
      className="inline-select-cell"
      title={disabled ? undefined : 'Click to edit'}
    >
      <span style={styles.displayText}>
        {currentOption?.label || (
          <span style={styles.placeholder}>{placeholder}</span>
        )}
      </span>
    </button>
  )
}

// ── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  editContainer: {
    position: 'relative',
    width: '100%',
  },
  select: {
    width: '100%',
    padding: '4px 8px',
    fontSize: '13px',
    color: 'var(--text-primary)',
    background: 'var(--bg-primary)',
    border: '2px solid var(--accent-primary)',
    borderRadius: 'var(--radius-sm)',
    outline: 'none',
    transition: 'border-color var(--transition-fast)',
    cursor: 'pointer',
  },
  selectError: {
    borderColor: 'var(--error)',
  },
  error: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: '4px',
    padding: '4px 8px',
    fontSize: '11px',
    color: 'var(--error)',
    background: 'var(--bg-elevated)',
    border: '1px solid var(--error)',
    borderRadius: 'var(--radius-sm)',
    whiteSpace: 'nowrap',
    zIndex: 10,
  },
  display: {
    display: 'inline-flex',
    alignItems: 'center',
    width: '100%',
    padding: '4px 8px',
    margin: '-4px -8px',
    fontSize: '13px',
    color: 'var(--text-primary)',
    background: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
  },
  displayHover: {
    background: 'var(--bg-tertiary)',
  },
  displayDisabled: {
    cursor: 'default',
    opacity: 0.7,
  },
  displayText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  placeholder: {
    color: 'var(--text-muted)',
    fontStyle: 'italic',
  },
}

export default InlineSelectCell
